import { useMemo, useState } from 'react';
import {
  useCreateManualOffer,
  usePreisToolSuppliers,
} from '@/api/preis-tool/preis-tool.hooks';
import { DsAlert, DsButton, DsCombobox, DsInput, DsModal } from '@/modules/internal-v2/ds';
import { newSupplierLabel, parsePriceInput, supplierGroups } from '../helpers';

interface ManualOfferDialogProps {
  itemId: string;
  itemName: string;
  onClose: () => void;
}

/** Quick choices for how long a phone or e-mail offer usually holds. */
const VALIDITY_DAYS = [3, 7, 14] as const;

/** Local calendar day `days` from today as `YYYY-MM-DD`. */
function dayFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Enter a supplier offer received by phone or e-mail for one item (EOS-60).
 * The offer is in EUR, stands next to the feed offers until its valid-to date
 * and can only be deleted afterwards, not edited.
 */
export function ManualOfferDialog({
  itemId,
  itemName,
  onClose,
}: ManualOfferDialogProps) {
  const { data: supplierData } = usePreisToolSuppliers();
  const create = useCreateManualOffer();
  const suppliers = supplierData?.suppliers ?? [];
  const supplierOptions = useMemo(() => supplierGroups(supplierData), [supplierData]);

  const [supplierName, setSupplierName] = useState('');
  const [netPrice, setNetPrice] = useState('');
  const [minQty, setMinQty] = useState('1');
  const [leadTimeDays, setLeadTimeDays] = useState('');
  const [quantity, setQuantity] = useState('');
  const [validTo, setValidTo] = useState(dayFromToday(7));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const name = supplierName.trim();
    const price = parsePriceInput(netPrice);
    const qty = minQty.trim() === '' ? 1 : Number(minQty);
    const lead = leadTimeDays.trim() === '' ? undefined : Number(leadTimeDays);
    // "1.200" is 1200 pieces.
    const pieces = quantity.trim() === '' ? undefined : Number(quantity.trim().replace(/[.\s]/g, ''));
    if (!name) return setError('Bitte einen Lieferanten angeben.');
    if (price == null || price <= 0) {
      return setError('Bitte einen Nettopreis größer 0 angeben.');
    }
    if (!Number.isInteger(qty) || qty < 1) {
      return setError('Die Mindestbestellmenge muss eine ganze Zahl ab 1 sein.');
    }
    if (lead !== undefined && (!Number.isInteger(lead) || lead < 0)) {
      return setError('Die Lieferzeit muss eine ganze Zahl in Tagen sein.');
    }
    if (pieces !== undefined && (!Number.isInteger(pieces) || pieces < 0)) {
      return setError('Die Menge muss eine ganze Zahl sein.');
    }
    if (!validTo) return setError('Bitte ein Gültigkeitsdatum wählen.');
    if (validTo < dayFromToday(0)) {
      return setError('Das Gültigkeitsdatum liegt in der Vergangenheit.');
    }
    setError(null);

    const known = suppliers.find(
      (s) => s.name.trim().toLowerCase() === name.toLowerCase(),
    );
    create.mutate(
      {
        itemId,
        supplierName: name,
        supplierBpId: known?.id,
        netPrice: price,
        minQty: qty,
        leadTimeDays: lead,
        quantity: pieces,
        validTo,
      },
      { onSuccess: onClose },
    );
  };

  const shownError =
    error ??
    (create.isError ? 'Das Angebot konnte nicht gespeichert werden.' : null);

  return (
    <DsModal
      open
      onClose={onClose}
      title="Angebot erfassen"
      description={`${itemId} · ${itemName}`}
      footer={
        <>
          <DsButton variant="ghost" onClick={onClose} disabled={create.isPending}>
            Abbrechen
          </DsButton>
          <DsButton variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Speichern…' : 'Angebot speichern'}
          </DsButton>
        </>
      }
    >
      <div className="pt-form">
        {shownError && <DsAlert severity="error">{shownError}</DsAlert>}
        <label className="pt-field">
          <span className="pt-label">Lieferant</span>
          <DsCombobox
            value={supplierName}
            onChange={setSupplierName}
            groups={supplierOptions}
            freeText={{ createLabel: newSupplierLabel }}
            placeholder="Name wählen oder frei eintippen"
            maxLength={200}
            autoFocus
          />
        </label>
        <div className="pt-field-row">
          <label className="pt-field">
            <span className="pt-label">Nettopreis (€)</span>
            <DsInput
              mono
              value={netPrice}
              onChange={(e) => setNetPrice(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
            />
          </label>
          <label className="pt-field">
            <span className="pt-label">Mindestbestellmenge</span>
            <DsInput
              mono
              value={minQty}
              onChange={(e) => setMinQty(e.target.value)}
              inputMode="numeric"
            />
          </label>
          <label className="pt-field">
            <span className="pt-label">Lieferzeit (Tage, optional)</span>
            <DsInput
              mono
              value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
              inputMode="numeric"
            />
          </label>
        </div>
        <div className="pt-field-row">
          <label className="pt-field">
            <span className="pt-label">Menge (Stück, optional)</span>
            <DsInput
              mono
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="z. B. 120"
              inputMode="numeric"
            />
          </label>
        </div>
        <div className="pt-field">
          <span className="pt-label">Gültig bis (einschließlich)</span>
          <div className="pt-field-row" style={{ alignItems: 'center' }}>
            <DsInput
              type="date"
              value={validTo}
              onChange={(e) => setValidTo(e.target.value)}
              style={{ flex: '0 0 150px' }}
            />
            {VALIDITY_DAYS.map((days) => (
              <DsButton
                key={days}
                size="small"
                variant={validTo === dayFromToday(days) ? 'primary' : 'default'}
                onClick={() => setValidTo(dayFromToday(days))}
              >
                {days} Tage
              </DsButton>
            ))}
          </div>
        </div>
        <div className="pt-fix-hint">
          Das Angebot wird in Euro erfasst, steht bis zum Gültigkeitsdatum
          neben den automatisch abgerufenen Angeboten und wird von keinem
          Abruf überschrieben. Ändern heißt: löschen und neu erfassen.
        </div>
      </div>
    </DsModal>
  );
}
